import { computed } from 'vue'
import { useLanguageStore } from '@/stores/language'
import { rtlUtils } from '@/utils/rtl'

export const useDirection = () => {
  const languageStore = useLanguageStore()

  // Computed properties for easy access
  const isRtl = computed(() => languageStore.isRtl)
  const direction = computed(() => languageStore.direction)
  const currentLanguage = computed(() => languageStore.currentLanguage)

  // Helper function to get RTL-aware positioning classes
  const getPositionClass = (position) => {
    if (!isRtl.value) return position

    const rtlMap = {
      'left': 'right',
      'right': 'left',
      'ml': 'mr',
      'mr': 'ml',
      'pl': 'pr',
      'pr': 'pl',
      'border-l': 'border-r',
      'border-r': 'border-l',
      'rounded-l': 'rounded-r',
      'rounded-r': 'rounded-l',
      'text-left': 'text-right',
      'text-right': 'text-left'
    }

    return rtlMap[position] || position
  }

  // Helper function to generate direction-aware CSS classes
  const getDirectionClasses = (classes) => {
    return rtlUtils.transformClasses(classes, direction.value)
  }

  // Helper function for flex direction classes
  const getFlexDirection = (baseDirection = 'flex-row') => {
    return rtlUtils.getFlexDirection(direction.value, baseDirection)
  }

  // Helper function for transform classes (especially useful for icons and arrows)
  const getTransformClass = (transform) => {
    if (!isRtl.value) return transform
    
    // Handle rotation and scale transforms for RTL
    const transformMap = {
      'rotate-90': 'rotate-90',
      'rotate-180': 'rotate-180',
      'rotate-270': 'rotate-270',
      '-rotate-90': '-rotate-90',
      'scale-x-100': '-scale-x-100',
      '-scale-x-100': 'scale-x-100'
    }
    
    return transformMap[transform] || transform
  }

  // Helper for spacing utilities with automatic RTL conversion
  const spacing = {
    marginLeft: (value) => rtlUtils.getSpacing('ml', value, direction.value),
    marginRight: (value) => rtlUtils.getSpacing('mr', value, direction.value),
    paddingLeft: (value) => rtlUtils.getSpacing('pl', value, direction.value),
    paddingRight: (value) => rtlUtils.getSpacing('pr', value, direction.value),
    borderLeft: (value = '') => rtlUtils.getSpacing('border-l', value, direction.value),
    borderRight: (value = '') => rtlUtils.getSpacing('border-r', value, direction.value),
    roundedLeft: (value = '') => rtlUtils.getSpacing('rounded-l', value, direction.value),
    roundedRight: (value = '') => rtlUtils.getSpacing('rounded-r', value, direction.value)
  }

  // Helper for text alignment
  const textAlign = {
    left: () => rtlUtils.getTextAlign('left', direction.value),
    right: () => rtlUtils.getTextAlign('right', direction.value),
    start: () => rtlUtils.getTextAlign('start', direction.value),
    end: () => rtlUtils.getTextAlign('end', direction.value)
  }

  // Helper for positioning
  const position = {
    left: (value) => rtlUtils.getPosition('left', value, direction.value),
    right: (value) => rtlUtils.getPosition('right', value, direction.value)
  }

  // Helper function to generate RTL-safe icon classes
  const getIconDirection = (shouldFlip = true) => {
    return shouldFlip && isRtl.value ? 'scale-x-[-1]' : ''
  }

  return {
    // State
    isRtl,
    direction,
    currentLanguage,
    
    // Helper functions
    getPositionClass,
    getDirectionClasses,
    getFlexDirection,
    getTransformClass,
    getIconDirection,
    
    // Utility objects
    spacing,
    textAlign,
    position,
    
    // Store actions
    toggleLanguage: languageStore.toggleLanguage,
    setLanguage: languageStore.setLanguage,
    getCurrentLanguage: languageStore.getCurrentLanguage
  }
}